import { useQueryClient } from "react-query";
import { useCallback } from "react";
import PatientDisposition from "../types/PatientDisposition";
import EDD from "../types/EDD";
import MedicallyReadyStatus from "../types/MedicallyReadyStatus";

interface UseUpdatePatientCacheFunctions {
  updatePatientDispositions: (
    patientID: number,
    updater: (current: PatientDisposition[] | undefined) => PatientDisposition[]
  ) => void;
  updatePatientEDDs: (
    patientID: number,
    updater: (current: EDD[] | undefined) => EDD[]
  ) => void;
  updatePatientMedicallyReady: (
    patientID: number,
    updater: (current: MedicallyReadyStatus | undefined) => MedicallyReadyStatus
  ) => void;
  upsertDisposition: (
    patientID: number,
    updatedDisposition: PatientDisposition
  ) => void;
  removeDisposition: (patientID: number, dispositionId: number) => void;
  upsertEDD: (patientID: number, updatedEDD: EDD) => void;
  updateSuggestedDispositionStatus: (
    patientID: number,
    suggestionId: number,
    status: string
  ) => void;
  updateSuggestedEDDStatus: (
    patientID: number,
    suggestionId: number,
    status: string
  ) => void;
  invalidateAllListQueries: () => void;
}

/**
 * Hook for directly updating patient data in the list cache
 * instead of invalidating and refetching all data.
 */
export const useUpdatePatientCache = (): UseUpdatePatientCacheFunctions => {
  const queryClient = useQueryClient();

  /**
   * Update a patient's dispositions in the list cache
   */
  const updatePatientDispositions = useCallback(
    (
      patientID: number,
      updater: (
        current: PatientDisposition[] | undefined
      ) => PatientDisposition[]
    ) => {
      queryClient.setQueriesData<Record<number, PatientDisposition[]>>(
        { queryKey: ["patientdispositionslist"] },
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            [patientID]: updater(oldData[patientID]),
          };
        }
      );
    },
    [queryClient]
  );

  /**
   * Update a patient's EDDs in the list cache
   */
  const updatePatientEDDs = useCallback(
    (patientID: number, updater: (current: EDD[] | undefined) => EDD[]) => {
      queryClient.setQueriesData<Record<number, EDD[]>>(
        { queryKey: ["eddlist"] },
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            [patientID]: updater(oldData[patientID]),
          };
        }
      );
    },
    [queryClient]
  );

  /**
   * Update a patient's medically ready status in the list cache
   */
  const updatePatientMedicallyReady = useCallback(
    (
      patientID: number,
      updater: (
        current: MedicallyReadyStatus | undefined
      ) => MedicallyReadyStatus
    ) => {
      queryClient.setQueriesData<Record<number, MedicallyReadyStatus>>(
        { queryKey: ["medicallyreadylist"] },
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            [patientID]: updater(oldData[patientID]),
          };
        }
      );
    },
    [queryClient]
  );

  /**
   * Helper: Update or add a disposition for a patient
   */
  const upsertDisposition = useCallback(
    (patientID: number, updatedDisposition: PatientDisposition) => {
      updatePatientDispositions(patientID, (current) => {
        if (!current) return [updatedDisposition];

        const exists = current.some((d) => d.id === updatedDisposition.id);
        if (exists) {
          return current.map((d) =>
            d.id === updatedDisposition.id ? updatedDisposition : d
          );
        }
        return [...current, updatedDisposition];
      });
    },
    [updatePatientDispositions]
  );

  /**
   * Helper: Remove a disposition by ID
   */
  const removeDisposition = useCallback(
    (patientID: number, dispositionId: number) => {
      updatePatientDispositions(patientID, (current) => {
        if (!current) return [];
        return current.filter((d) => d.id !== dispositionId);
      });
    },
    [updatePatientDispositions]
  );

  /**
   * Helper: Update or add an EDD for a patient
   */
  const upsertEDD = useCallback(
    (patientID: number, updatedEDD: EDD) => {
      updatePatientEDDs(patientID, (current) => {
        if (!current) return [updatedEDD];

        const exists = current.some((e) => e.id === updatedEDD.id);
        if (exists) {
          return current.map((e) => (e.id === updatedEDD.id ? updatedEDD : e));
        }
        return [...current, updatedEDD];
      });
    },
    [updatePatientEDDs]
  );

  /**
   * Helper: Mark a suggested disposition as accepted/rejected
   */
  const updateSuggestedDispositionStatus = useCallback(
    (patientID: number, suggestionId: number, status: string) => {
      updatePatientDispositions(patientID, (current) => {
        if (!current) return [];
        return current.map((d) =>
          d.id === suggestionId ? { ...d, status } : d
        );
      });
    },
    [updatePatientDispositions]
  );

  /**
   * Helper: Mark a suggested EDD as accepted/rejected
   */
  const updateSuggestedEDDStatus = useCallback(
    (patientID: number, suggestionId: number, status: string) => {
      updatePatientEDDs(patientID, (current) => {
        if (!current) return [];
        return current.map((e: EDD) =>
          e.id === suggestionId ? { ...e, status } : e
        );
      });
    },
    [updatePatientEDDs]
  );

  /**
   * Invalidate all list queries (for background refresh)
   */
  const invalidateAllListQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["patientdispositionslist"] });
    queryClient.invalidateQueries({ queryKey: ["eddlist"] });
    queryClient.invalidateQueries({ queryKey: ["medicallyreadylist"] });
    queryClient.invalidateQueries({ queryKey: ["patientmilestoneslist"] });
    queryClient.invalidateQueries({ queryKey: ["patientclinicalinsightslist"] });
    queryClient.invalidateQueries({ queryKey: ["intelligencelist"] });
    queryClient.invalidateQueries({ queryKey: ["patientinsightslist"] });
    queryClient.invalidateQueries({ queryKey: ["noteslist"] });
  }, [queryClient]);

  return {
    // Core updaters
    updatePatientDispositions,
    updatePatientEDDs,
    updatePatientMedicallyReady,
    // Convenience helpers
    upsertDisposition,
    removeDisposition,
    upsertEDD,
    updateSuggestedDispositionStatus,
    updateSuggestedEDDStatus,
    // Full invalidation for background refresh
    invalidateAllListQueries,
  };
};
